import { component, DemoBox, demoFile } from 'system/demo';
import { fromNow, fromNowCompact, DateTimeDisplay, formatToDateRangeString, MonthAbbreviation } from '../date';
import { Heading, UtilityText } from '../text';
import { Pad, HorizBox, VertBox } from '../basics';
import { ScrollView } from 'react-native';    
import { useLanguage } from 'component/translation';
import { toTitleCase } from 'util/util';

demoFile('component/date');

const now = new Date().getTime();
const thirtySecondsAgo = now - 30 * 1000;
const aMinuteAgo = now - 60 * 1000;
const twoMinutesAgo = now - 2 * 60 * 1000;
const anHourAgo = now - 60 * 60 * 1000;
const threeHoursAgo = now - 3 * 60 * 60 * 1000;
const aDayAgo = now - 24 * 60 * 60 * 1000;
const twoDaysAgo = now - 30 * 24 * 60 * 60 * 1000;
const aMonthAgo = now - 31 * 24 * 60 * 60 * 1000;
const aYearAgo = now - 365 * 24 * 60 * 60 * 1000;


function FormatDistanceToNowDemo() {
    const testDates = [
        { label: '< 60 seconds', date: thirtySecondsAgo },
        { label: '60-119 seconds', date: aMinuteAgo },
        { label: '2 minutes', date: twoMinutesAgo },
        { label: '1 hour', date: anHourAgo },
        { label: '3 hours', date: threeHoursAgo },
        { label: '24h +', date: aDayAgo },
        { label: '48h +', date: twoDaysAgo },
        { label: 'Different year +', date: aYearAgo }
    ];

    const language = useLanguage();

    return <DemoBox>
        <UtilityText text={`Formatting Concept: time displays as "1 minute ago" until it reaches 2 minutes, "1 hour ago" until 2 hours, etc.`} type='small' weight='medium' />
        <Pad size={8} />
        <HorizBox>
            <HorizBox gap={40}>
                <VertBox gap={8} wide>
                    <Heading text={'Timeframe'} />
                    <VertBox gap={8}>
                        {testDates.map(({label}) => 
                            <HorizBox key={label} gap={4}>
                                <UtilityText text={label} type='small' weight='medium' />
                            </HorizBox>
                        )}
                    </VertBox>
                    <Pad size={16} />
                </VertBox>
            </HorizBox>
            <Pad size={64} />
            <HorizBox gap={40}>
                <VertBox gap={8}>
                    <Heading text={toTitleCase(language.replace(/_/g, ' '))} />
                    {testDates.map(({date}) => 
                        <HorizBox key={date.toString()} gap={4}>
                            <UtilityText text={fromNow(date, language)} type='small' />
                        </HorizBox>
                    )}
                    <Pad size={16} />
                </VertBox>
            </HorizBox>
        </HorizBox>
    </DemoBox>
}
component('formatDistanceToNow', FormatDistanceToNowDemo);

function FormatDistanceToNowCompactDemo() {
    
    /** @see setuptests.ts use fixed date in tests */
    const now = process.env.NODE_ENV === 'test' ? new Date('2024-01-01T12:00:00') : new Date();

    const miniTestDates = [
        { label: 'Now', timePastInMs: now.getTime() },
        { label: 'A minute ago', timePastInMs: now.getTime() - 60 * 1000 },
        { label: 'An hour ago', timePastInMs: now.getTime() - 60 * 60 * 1000 },
        { label: 'A day ago', timePastInMs: now.getTime() - 24 * 60 * 60 * 1000 },
        { label: 'A month ago', timePastInMs: now.getTime() - 31 * 24 * 60 * 60 * 1000 },
        { label: 'A year ago', timePastInMs: now.getTime() - 365 * 24 * 60 * 60 * 1000 }
    ];

    const language = useLanguage(); 

    return <DemoBox>
        <HorizBox>
            <HorizBox gap={40}>
                <VertBox gap={8}>
                    <Heading text={'Timeframe'} />
                    <VertBox gap={8}>
                        {miniTestDates.map(({label}) => 
                            <HorizBox key={label} gap={4}>
                                <UtilityText text={label} type='small' weight='medium' />
                            </HorizBox>
                        )}
                    </VertBox>
                    <Pad size={16} />
                </VertBox>
            </HorizBox>
            <Pad size={64} />
            <ScrollView horizontal>
                <HorizBox gap={40}>
                    <VertBox gap={8}>
                        <Heading text={toTitleCase(language.replace(/_/g, ' '))} />
                        {miniTestDates.map(({timePastInMs}) => 
                            <HorizBox key={timePastInMs} gap={4}>
                                <UtilityText text={fromNowCompact(timePastInMs, language)} type='small' />
                            </HorizBox>
                        )}
                        <Pad size={16} />
                    </VertBox>
                </HorizBox>
            </ScrollView>
        </HorizBox>
    </DemoBox>
}
component('formatDistanceToNowCompact', FormatDistanceToNowCompactDemo);


function DateTimeDisplayDemo() {
    const language = useLanguage();
    return <DemoBox>
        <ScrollView horizontal>
            <HorizBox gap={40}>
                <VertBox gap={8}>
                    <Heading text={toTitleCase(language.replace(/_/g, ' '))} />
                    <DateTimeDisplay timestamp={new Date().getTime()} />
                    <Pad size={16} />
                </VertBox>
            </HorizBox>
        </ScrollView>
    </DemoBox>
}
component('DateTimeDisplay', DateTimeDisplayDemo);


function FormatToDateRangeStringDemo() {
    const sameMonthStart = new Date(2024, 4, 10);
    const sameMonthEnd = new Date(2024, 4, 20);
    const multiMonthStart = new Date(2024, 4, 10);
    const multiMonthEnd = new Date(2024, 5, 20);

    return <DemoBox>
        <UtilityText text={`English (same month): ${formatToDateRangeString({start: sameMonthStart, end: sameMonthEnd, locale: 'en-US'})}`} />
        <UtilityText text={`English (multi month): ${formatToDateRangeString({start: multiMonthStart, end: multiMonthEnd, locale: 'en-US'})}`} />
        <UtilityText text={`German (same month): ${formatToDateRangeString({start: sameMonthStart, end: sameMonthEnd, locale: 'de-DE'})}`} />
        <UtilityText text={`German (multi month): ${formatToDateRangeString({start: multiMonthStart, end: multiMonthEnd, locale: 'de-DE'})}`} />
        <UtilityText text={`French (same month): ${formatToDateRangeString({start: sameMonthStart, end: sameMonthEnd, locale: 'fr-CA'})}`} />
        <UtilityText text={`French (multi month): ${formatToDateRangeString({start: multiMonthStart, end: multiMonthEnd, locale: 'fr-CA'})}`} />
    </DemoBox>
}
component('formatToDateRangeString', FormatToDateRangeStringDemo);


function GetMonthAbbreviationDemo() {
    const year = 2024;
    const months = Array.from({length: 12}, (_, i) => new Date(year, i, 1));
    const locales = [
        {name: 'English', locale: 'en-US'},
        {name: 'German', locale: 'de-DE'},
        {name: 'French Canadian', locale: 'fr-CA'},
        {name: 'French', locale: 'fr-FR'}
    ]

    return <DemoBox>
        <HorizBox gap={40}>
            {locales.map(({name, locale}) => 
                <VertBox key={name} gap={8}>
                    <Heading text={name} />
                    {months.map((date, i) => 
                        <MonthAbbreviation key={i} date={date} locale={locale} />
                    )}
                </VertBox>
            )}
        </HorizBox>
    </DemoBox>
}
component('getMonthAbbreviation', GetMonthAbbreviationDemo);
